"use client";
import CorporateTrainingPackageForm from "@/components/forms/CorporateTrainingPackageForm";
import Section from "@/components/Section";
import { Button, Card, CardContent, Dialog, Grid2, Typography } from "@repo/ui";
import { Check, Star, Zap, Users, Building2, Crown } from "lucide-react";
import { MouseEventHandler, useState } from "react";

const list = [
  {
    name: "1 License = 8 Hours of Training",
    description: [
      "Each license corresponds to a full day of training, which is 8 hours.",
    ],
  },
  {
    name: "Half License = 4 Hours of Training",
    description: [
      "If a session is 4 hours, it will correspond to half a license.",
    ],
  },
  {
    name: "Training Day Definition:",
    description: [
      "A training day is defined as 8 hours of instructor-led training.",
      "For technical programs or shorter sessions, such as 4-hour sessions, the trainer will still charge for a full 8-hour day of training, regardless of the shorter duration.",
    ],
  },
];

const CorporateTrainingPackages = () => {
  const [open, setOpen] = useState(false);
  const [pack, setPackage] = useState<string | undefined>("starter");
  const handleOpen: MouseEventHandler<HTMLButtonElement> = (event) => {
    setPackage(event.currentTarget.dataset.package);
    setOpen(true);
  };

  return (
    <Section
      className=""
      id=""
      title="Customized Instructor-led Corporate Training Prices and Packages for Organizations Globally"
      color="primary"
    >
      <div className="pt-8">
        {/* Pricing Cards Grid */}
        <div className="grid lg:grid-cols-4 gap-4 lg:gap-6">
          {/* Starter Plan */}
          <div className="relative group/pc">
            <div className="absolute inset-0 bg-secondary-main rounded-3xl blur-xl opacity-20  transition-opacity duration-500 group-hover/pc:opacity-50"></div>
            <div className="relative bg-common-paper/5 backdrop-blur-xl border border-common-paper/20 rounded-3xl p-8 shadow-xl hover:translate-y-[-4px] transition-all duration-300">
              <div className="inline-block p-3 bg-secondary-main/10 rounded-2xl mb-6">
                <Zap className="h-6 w-6 text-secondary-main" />
              </div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-common-paper mb-2">
                  Starter
                </h2>
                <p className="text-common-paper/80">Perfect for small teams</p>
                <div className="mt-6 flex items-baseline">
                  <span className="text-5xl font-extrabold text-common-paper">
                    120
                  </span>
                  <span className="text-common-paper/80 ml-2">licenses</span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>64 hours of Group Training</span>
                </li>
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>Up to 10 training sessions</span>
                </li>
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>2000+ Training programs</span>
                </li>
              </ul>

              <Button
                fullWidth
                onClick={handleOpen}
                variant="outlined"
                color="secondary"
                data-package="starter"
              >
                Get Started
              </Button>
            </div>
          </div>

          {/* Growth Plan */}
          <div className="relative group/pc lg:scale-105">
            <div className="absolute inset-0 bg-secondary-main rounded-3xl blur-xl opacity-40 transition-opacity duration-500 group-hover/pc:opacity-50"></div>
            <div className="relative bg-common-paper/5 backdrop-blur-xl border border-common-paper/20 rounded-3xl p-8 shadow-xl hover:translate-y-[-4px] transition-all duration-300">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-secondary-main text-common-paper px-3 py-2 rounded-full text-xs font-semibold flex items-center shadow-lg">
                  <Star className="h-4 w-4 mr-1" />
                  Most Popular
                </span>
              </div>

              <div className="inline-block p-3 bg-secondary-main/10 rounded-2xl mb-6">
                <Users className="h-6 w-6 text-secondary-main" />
              </div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-common-paper mb-2">
                  Growth
                </h2>
                <p className="text-common-paper/80">
                  For growing organizations
                </p>
                <div className="mt-6 flex items-baseline">
                  <span className="text-5xl font-extrabold text-common-paper">
                    320
                  </span>
                  <span className="text-common-paper/80 ml-2">licenses</span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>160 hours of Group Training</span>
                </li>
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>Up to 25 training sessions</span>
                </li>
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>2000+ Training programs</span>
                </li>
              </ul>

              <Button
                onClick={handleOpen}
                color="secondary"
                fullWidth
                data-package="growth"
              >
                Get Started
              </Button>
            </div>
          </div>

          {/* Enterprise Plan */}
          <div className="relative group/pc">
            <div className="absolute inset-0 bg-secondary-main rounded-3xl blur-xl opacity-20  transition-opacity duration-500 group-hover/pc:opacity-50"></div>
            <div className="relative bg-common-paper/5 backdrop-blur-xl border border-common-paper/20 rounded-3xl p-8 shadow-xl hover:translate-y-[-4px] transition-all duration-300">
              <div className="inline-block p-3 bg-secondary-main/10 rounded-2xl mb-6">
                <Building2 className="h-6 w-6 text-secondary-main" />
              </div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-common-paper mb-2">
                  Enterprise
                </h2>
                <p className="text-common-paper/80">For large organizations</p>
                <div className="mt-6 flex items-baseline">
                  <span className="text-5xl font-extrabold text-common-paper">
                    800
                  </span>
                  <span className="text-common-paper/80 ml-2">licenses</span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>400 hours of Group Training</span>
                </li>
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>Up to 60 training sessions</span>
                </li>
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>2000+ Training programs</span>
                </li>
              </ul>

              <Button
                fullWidth
                onClick={handleOpen}
                variant="outlined"
                color="secondary"
                data-package="enterprise"
              >
                Get Started
              </Button>
            </div>
          </div>

          {/* Custom Plan */}
          <div className="relative group/pc">
            <div className="absolute inset-0 bg-secondary-main rounded-3xl blur-xl opacity-20  transition-opacity duration-500 group-hover/pc:opacity-50"></div>
            <div className="relative bg-common-paper/5 backdrop-blur-xl border border-common-paper/20 rounded-3xl p-8 shadow-xl hover:translate-y-[-4px] transition-all duration-300">
              <div className="inline-block p-3 bg-secondary-main/10 rounded-2xl mb-6">
                <Crown className="h-6 w-6 text-secondary-main" />
              </div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-common-paper mb-2">
                  Custom
                </h2>
                <p className="text-common-paper/80">Tailored to your needs</p>
                <div className="mt-6 flex items-baseline">
                  <span className="text-5xl font-extrabold text-common-paper">
                    ∞
                  </span>
                  <span className="text-common-paper/80 ml-2">unlimited</span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>Unlimited Training Hours</span>
                </li>
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>Unlimited Training Sessions</span>
                </li>
                <li className="flex items-center text-common-paper/90">
                  <Check className="h-5 w-5 text-secondary-main mr-3" />
                  <span>Custom Training Programs</span>
                </li>
              </ul>

              <Button
                fullWidth
                onClick={handleOpen}
                variant="outlined"
                color="secondary"
                data-package="custom"
              >
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Grid2 spacing={5} container className="mt-20" align={"center"}>
        <Grid2 size={{ md: 7, lg: 7, sm: 12 }}>
          <ol>
            {list.map((item, index) => (
              <li key={index} className="mb-4">
                <span className="flex items-center gap-2 ">
                  <span className="bg-primary-light px-1.5 py-0.5 rounded-full text-white text-xs flex items-center justify-center w-5 h-5">
                    {index + 1}
                  </span>
                  {item.name}
                </span>
                <ul className="list-['-_']  ml-10">
                  {item.description.map((desc, descIndex) => (
                    <li key={descIndex} className="">
                      {desc}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </Grid2>
        <Grid2 size={{ md: 5, lg: 5, sm: 12 }}>
          <Card outlined className="text-gray-700">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                What is not included?
              </Typography>
              <Typography>
                Certification/exam fees, post-training support, and labs are not
                included in the pricing and will be billed separately. The
                pricing for onsite training does not cover facilities and
                catering services. Certain customized and advanced training
                sessions may incur additional charges.
              </Typography>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
      <Dialog
        open={open}
        onOpenChange={(open) => setOpen(open)}
        title="Corporate Training Packages"
      >
        <CorporateTrainingPackageForm
          packageName={pack}
          onSuccess={() => setOpen(false)}
        />
      </Dialog>
    </Section>
  );
};

export default CorporateTrainingPackages;
